import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Menu,
  X,
  ArrowUp,
  Users,
  FileText,
  Send,
  CheckCircle2,
  Building2,
  ShoppingBag,
  GraduationCap,
  Lightbulb,
  Store,
  Heart,
  Scissors,
  Home,
  Car,
  Smartphone,
  Palette,
  Database,
  Leaf,
  Zap,
  ChevronRight,
  Linkedin,
  Twitter,
  Mail,
  Phone,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SUPABASE_URL = "https://jmveeciwmeoebomfsxxf.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImptdmVlY2l3bWVvZWJvbWZzeHhmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEzNDMxMzAsImV4cCI6MjA4NjkxOTEzMH0.GVMgPU7K5FuRJuUTyt8-r9qiHgdKfFFOXH8zNgO9qak";
// Customer logos for marquee
const CUSTOMER_LOGOS = [
  { name: "Asset Marker", src: "/logos/Asset Marker.jpeg" },
  { name: "Cake & Co.", src: "/logos/Cake & Co..jpg" },
  { name: "Event Makerz", src: "/logos/Event Makerz.jpeg" },
  { name: "Nyturals", src: "/logos/Nyturals.jpeg" },
  { name: "Print and Bind", src: "/logos/Print and Bind.jpeg" },
  { name: "Skills Konnect", src: "/logos/Skills Konnect.jpeg" },
];

const BUSINESS_TYPES = [
  { value: "Exhibition/Event", label: "Exhibition/Event" },
  { value: "Retail Store", label: "Retail Store" },
  { value: "Educational Institute", label: "Educational Institute" },
  { value: "Consultant/Counselor", label: "Consultant/Counselor" },
  { value: "Small Business", label: "Small Business" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "Salon/Spa", label: "Salon/Spa" },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Car Dealership", label: "Car Dealership" },
  { value: "Other", label: "Other" },
];

const USE_CASES = [
  {
    icon: Building2,
    title: "Exhibition Booths",
    description: "Share product info with booth visitors without printing hundreds of brochures",
  },
  {
    icon: ShoppingBag,
    title: "Retail Stores",
    description: "Send digital catalogs and collect customer emails for future promotions",
  },
  {
    icon: GraduationCap,
    title: "Educational Institutes",
    description: "Share course catalogs, admission info, and event details instantly with prospective students",
  },
  {
    icon: Lightbulb,
    title: "Independent Counselors",
    description: "Career coaches, education consultants, business advisors - share resources instantly and capture leads",
  },
  {
    icon: Store,
    title: "Small Businesses",
    description: "Perfect for any business that meets customers in person and wants to stay connected",
  },
  {
    icon: Heart,
    title: "Healthcare Clinics",
    description: "Send health information, service details, and appointment reminders to patients",
  },
  {
    icon: Scissors,
    title: "Salons & Spas",
    description: "Share service menus, packages, and booking information with clients",
  },
  {
    icon: Home,
    title: "Real Estate",
    description: "Send property brochures and floor plans instantly to interested buyers",
  },
  {
    icon: Car,
    title: "Car Dealerships",
    description: "Share vehicle brochures and test drive confirmations with prospects",
  },
];

const BENEFITS = [
  {
    icon: Smartphone,
    title: "No App Downloads",
    description: "Works on any device, no installation needed. Visitors just enter their details and go.",
  },
  {
    icon: Palette,
    title: "Your Branding",
    description: "Every email shows your logo and colors. Professional, personalized communication.",
  },
  {
    icon: Database,
    title: "Lead Capture",
    description: "All contact info automatically saved to your LeadCrew dashboard for future marketing.",
  },
  {
    icon: Leaf,
    title: "Go Paperless",
    description: "Save printing costs and help the environment. Digital collaterals are eco-friendly.",
  },
  {
    icon: Zap,
    title: "Instant Follow-up",
    description: "Collaterals delivered in seconds, not days. Strike while interest is hot.",
  },
];

const HOW_IT_WORKS = [
  {
    icon: Users,
    title: "Visitor Shows Interest",
    description: "At your booth, store, or meeting",
  },
  {
    icon: FileText,
    title: "Quick Form",
    description: "They enter WhatsApp & email (takes 10 seconds)",
  },
  {
    icon: CheckCircle2,
    title: "Select Collaterals",
    description: "Choose brochures, catalogs, videos, documents they want",
  },
  {
    icon: Send,
    title: "Instant Delivery + Data Capture",
    description: "Branded email sent immediately + contact info saved to your LeadCrew dashboard",
  },
];

// Header Component
const Header = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
        <a
          href="/"
          className="flex items-center gap-2 text-2xl font-bold tracking-tight"
          data-testid="logo"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_leadcrew-preview/artifacts/c74tvwhe_Logo%20element%20and%20Favicon.png" 
            alt="LeadCrew Logo" 
            className="w-8 h-8 object-contain"
          />
          <span>
            <span className="brand-gradient">Lead</span>
            <span className="text-white">Crew</span>
          </span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          <button
            onClick={() => scrollToSection("how-it-works")}
            className="nav-link text-sm font-medium"
            data-testid="nav-how-it-works"
          >
            How It Works
          </button>
          <button
            onClick={() => scrollToSection("use-cases")}
            className="nav-link text-sm font-medium"
            data-testid="nav-use-cases"
          >
            Use Cases
          </button>
          <button
            onClick={() => scrollToSection("benefits")}
            className="nav-link text-sm font-medium"
            data-testid="nav-benefits"
          >
            Benefits
          </button>
          <Button
            onClick={() => scrollToSection("contact")}
            className="rounded-full bg-primary hover:bg-primary/90 text-white px-6 transition-all hover:scale-105"
            data-testid="nav-cta"
          >
            Get Started
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Menu className="w-6 h-6 text-white" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col gap-4">
          <button
            onClick={() => scrollToSection("how-it-works")}
            className="text-left py-2 text-white/80 hover:text-white transition-colors"
            data-testid="mobile-nav-how-it-works"
          >
            How It Works
          </button>
          <button
            onClick={() => scrollToSection("use-cases")}
            className="text-left py-2 text-white/80 hover:text-white transition-colors"
            data-testid="mobile-nav-use-cases"
          >
            Use Cases
          </button>
          <button
            onClick={() => scrollToSection("benefits")}
            className="text-left py-2 text-white/80 hover:text-white transition-colors"
            data-testid="mobile-nav-benefits"
          >
            Benefits
          </button>
          <Button
            onClick={() => scrollToSection("contact")}
            className="rounded-full bg-primary hover:bg-primary/90 text-white mt-2"
            data-testid="mobile-nav-cta"
          >
            Get Started
          </Button>
        </nav>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToHowItWorks = () => {
    const element = document.getElementById("how-it-works");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-20"
      data-testid="hero-section"
    >
      <div className="hero-glow" />
      <div className="geometric-shapes">
        <div className="shape shape-1" />
        <div className="shape shape-2" />
        <div className="shape shape-3" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center relative z-10">
        <div className="space-y-8 animate-fade-in-up">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
            <span className="text-white">Turn Every Customer Interaction Into a </span>
            <span className="brand-gradient">Marketing Opportunity</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
            Collect visitor contacts and send branded collaterals instantly - no apps, no hassle. Perfect for exhibitions, retail stores, consultants, and service businesses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={scrollToHowItWorks}
              size="lg"
              className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg transition-all hover:scale-105 animate-pulse-glow"
              data-testid="hero-cta"
            >
              See How It Works
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          <div className="flex items-center gap-6 pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-secondary" />
              <span className="text-sm text-muted-foreground">No App Required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-secondary" />
              <span className="text-sm text-muted-foreground">Instant Delivery</span>
            </div>
          </div>
        </div>

        <div className="relative hero-visual animate-fade-in delay-300">
          <img
            src="https://customer-assets.emergentagent.com/job_leadcrew-preview/artifacts/gnbgcny8_Center%20Image.png"
            alt="Abstract 3D inverted pyramid with blue cubes representing digital connectivity and lead flow"
            className="w-full h-auto rounded-3xl animate-float"
            loading="eager"
          />
          <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-secondary/20 rounded-2xl blur-2xl" />
          <div className="absolute -top-4 -right-4 w-32 h-32 bg-primary/20 rounded-2xl blur-2xl" />
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="py-20 md:py-32 section-gradient-1"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">
            How It Works
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            Simple. Fast. Professional.
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Four easy steps to capture leads and share your marketing materials instantly
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          <div className="hidden lg:block absolute top-16 left-[15%] right-[15%] h-0.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          
          {HOW_IT_WORKS.map((step, index) => (
            <div
              key={index}
              className={`relative text-center ${
                isVisible ? "animate-fade-in-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 150}ms` }}
              data-testid={`step-${index + 1}`}
            >
              <div className="step-icon mx-auto mb-6">
                <step.icon className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <div className="absolute -top-2 -left-2 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                {index + 1}
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">{step.title}</h3>
              <p className="text-muted-foreground text-sm">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Use Cases Section
const UseCasesSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="use-cases"
      ref={sectionRef}
      className="py-20 md:py-32 section-gradient-2"
      data-testid="use-cases-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium uppercase tracking-widest text-secondary mb-4">
            Use Cases
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            Perfect For Every Business Type
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            From exhibitions to retail - capture leads wherever you meet customers
          </p>
        </div>

        <div className="bento-grid">
          {USE_CASES.map((useCase, index) => (
            <div
              key={index}
              className={`bento-card card-hover ${
                isVisible ? "animate-fade-in-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`use-case-${index + 1}`}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <useCase.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">{useCase.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {useCase.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="benefits"
      ref={sectionRef}
      className="py-20 md:py-32 section-gradient-1 dot-pattern"
      data-testid="benefits-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">
            Benefits
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            Why Businesses Choose LeadCrew
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFITS.map((benefit, index) => (
            <div
              key={index}
              className={`feature-card ${
                isVisible ? "animate-fade-in-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`benefit-${index + 1}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/10 flex items-center justify-center mb-6">
                <benefit.icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">{benefit.title}</h3>
              <p className="text-muted-foreground leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Social Proof Section
const SocialProofSection = () => {
  const testimonials = [
    {
      quote: "LeadCrew transformed how we capture leads at trade shows. No more lost business cards!",
      author: "David Thomas",
      role: "Event Manager",
      image: "https://images.pexels.com/photos/14589344/pexels-photo-14589344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
      quote: "Our retail store now captures 3x more customer contacts. The instant email feature is brilliant.",
      author: "Shraddha S",
      role: "Retail Director",
      image: "https://images.pexels.com/photos/6572210/pexels-photo-6572210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
      quote: "Simple, effective, and professional. Our clients love receiving instant brochures.",
      author: "Michael Carvalho",
      role: "Marketing Lead",
      image: "https://images.unsplash.com/photo-1576558656222-ba66febe3dec?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
  ];

  return (
    <section
      id="social-proof"
      className="py-20 md:py-32 section-gradient-2"
      data-testid="social-proof-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium uppercase tracking-widest text-secondary mb-4">
            Testimonials
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            Trusted by Businesses Across India
          </h2>
        </div>

        {/* Logo Marquee */}
        <div className="mb-16 overflow-hidden">
          <div className="logo-marquee">
            <div className="logo-marquee-track">
              {/* First set of logos */}
              {CUSTOMER_LOGOS.map((logo, index) => (
                <div
                  key={`logo-1-${index}`}
                  className="logo-item"
                  data-testid={`customer-logo-${index + 1}`}
                >
                  <img
                    src={logo.src}
                    alt={logo.name}
                    className="h-12 md:h-14 w-auto object-contain filter brightness-100"
                  />
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {CUSTOMER_LOGOS.map((logo, index) => (
                <div
                  key={`logo-2-${index}`}
                  className="logo-item"
                >
                  <img
                    src={logo.src}
                    alt={logo.name}
                    className="h-12 md:h-14 w-auto object-contain filter brightness-100"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="testimonial-card"
              data-testid={`testimonial-${index + 1}`}
            >
              <div className="mb-6">
                <svg
                  className="w-10 h-10 text-primary/30"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>
              <p className="text-white/90 text-lg mb-6 leading-relaxed">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center gap-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.author}
                  className="w-12 h-12 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <p className="font-semibold text-white">{testimonial.author}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    business_type: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10,}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Please enter a valid phone number (at least 10 digits)";
    }

    if (!formData.business_type) {
      newErrors.business_type = "Please select a business type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(
        "https://jmveeciwmeoebomfsxxf.supabase.co/rest/v1/demo_requests",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImptdmVlY2l3bWVvZWJvbWZzeHhmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEzNDMxMzAsImV4cCI6MjA4NjkxOTEzMH0.GVMgPU7K5FuRJuUTyt8-r9qiHgdKfFFOXH8zNgO9qak",
            "Authorization": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImptdmVlY2l3bWVvZWJvbWZzeHhmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEzNDMxMzAsImV4cCI6MjA4NjkxOTEzMH0.GVMgPU7K5FuRJuUTyt8-r9qiHgdKfFFOXH8zNgO9qak",
            "Prefer": "return=minimal"
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            company: formData.company,
            phone: formData.phone,
            business_type: formData.business_type,
            message: formData.message,
          }),
        }
      );
      if (response.ok) {
        setIsSuccess(true);
        toast.success("Thank you! We'll be in touch within 24 hours 🙌");
        setFormData({
          name: "", email: "", company: "",
          phone: "", business_type: "", message: "",
        });
      } else {
        throw new Error("Failed to submit");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Sorry, something went wrong. Please email us at hello@leadcrew.in");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-32 section-gradient-1"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">
              Get Started
            </p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Ready to Capture More Leads?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
              Start collecting customer contacts the smart way. See how LeadCrew works for your business.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-secondary" />
                </div>
                <span className="text-white/90">Free demo and consultation</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-secondary" />
                </div>
                <span className="text-white/90">Customized for your industry</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-secondary" />
                </div>
                <span className="text-white/90">Setup in under 24 hours</span>
              </div>
            </div>
          </div>

          <div className="form-container">
            {isSuccess ? (
              <div className="text-center py-12 success-message" data-testid="success-message">
                <div className="w-20 h-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-secondary" />
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-white">Thank You!</h3>
                <p className="text-muted-foreground">
                  We'll be in touch within 24 hours 🙌
                </p>
                <Button
                  onClick={() => setIsSuccess(false)}
                  variant="outline"
                  className="mt-6 rounded-full border-white/10 text-white hover:bg-white/5"
                  data-testid="submit-another-btn"
                >
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="demo-request-form">
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-muted-foreground mb-2 block">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      className={`bg-white/5 border-white/10 focus:border-primary ${
                        errors.name ? "border-red-500" : ""
                      }`}
                      placeholder="Your name"
                      data-testid="input-name"
                    />
                    {errors.name && (
                      <p className="text-red-400 text-sm mt-1" data-testid="error-name">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-muted-foreground mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className={`bg-white/5 border-white/10 focus:border-primary ${
                        errors.email ? "border-red-500" : ""
                      }`}
                      placeholder="you@company.com"
                      data-testid="input-email"
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1" data-testid="error-email">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-sm font-medium text-muted-foreground mb-2 block">
                      Company *
                    </Label>
                    <Input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleChange("company", e.target.value)}
                      className={`bg-white/5 border-white/10 focus:border-primary ${
                        errors.company ? "border-red-500" : ""
                      }`}
                      placeholder="Your company name"
                      data-testid="input-company"
                    />
                    {errors.company && (
                      <p className="text-red-400 text-sm mt-1" data-testid="error-company">{errors.company}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-muted-foreground mb-2 block">
                      Phone *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className={`bg-white/5 border-white/10 focus:border-primary ${
                        errors.phone ? "border-red-500" : ""
                      }`}
                      placeholder="+91 98765 43210"
                      data-testid="input-phone"
                    />
                    {errors.phone && (
                      <p className="text-red-400 text-sm mt-1" data-testid="error-phone">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="business_type" className="text-sm font-medium text-muted-foreground mb-2 block">
                      Business Type *
                    </Label>
                    <Select
                      value={formData.business_type}
                      onValueChange={(value) => handleChange("business_type", value)}
                    >
                      <SelectTrigger
                        className={`bg-white/5 border-white/10 focus:border-primary ${
                          errors.business_type ? "border-red-500" : ""
                        }`}
                        data-testid="select-business-type"
                      >
                        <SelectValue placeholder="Select your business type" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUSINESS_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.business_type && (
                      <p className="text-red-400 text-sm mt-1" data-testid="error-business-type">
                        {errors.business_type}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-muted-foreground mb-2 block">
                      Message (Optional)
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      className="bg-white/5 border-white/10 focus:border-primary min-h-[100px]"
                      placeholder="Tell us about your needs..."
                      data-testid="input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-primary hover:bg-primary/90 text-white py-6 text-lg transition-all hover:scale-[1.02]"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? "Submitting..." : "Request Demo"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-16 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <a href="/" className="flex items-center gap-2 text-2xl font-bold tracking-tight mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_leadcrew-preview/artifacts/c74tvwhe_Logo%20element%20and%20Favicon.png" 
                alt="LeadCrew Logo" 
                className="w-8 h-8 object-contain"
              />
              <span>
                <span className="brand-gradient">Lead</span>
                <span className="text-white">Crew</span>
              </span>
            </a>
            <p className="text-muted-foreground max-w-md">
              Smart Lead Capture for Modern Businesses. Collect visitor contacts and send branded collaterals instantly.
            </p>
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://www.linkedin.com/company/yourleadcrew/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
                data-testid="social-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-white/70" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
                data-testid="social-twitter"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5 text-white/70" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4">Contact</h4>
            <div className="space-y-3">
              <a
                href="mailto:hello@leadcrew.in"
                className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
                data-testid="contact-email"
              >
                <Mail className="w-4 h-4" />
                hello@leadcrew.in
              </a>
              <a
                href="tel:+919999111606"
                className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
                data-testid="contact-phone"
              >
                <Phone className="w-4 h-4" />
                +91 9999111606
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4">Legal</h4>
            <div className="space-y-3">
              <a
                href="/privacy"
                className="block text-muted-foreground hover:text-white transition-colors"
                data-testid="link-privacy"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className="block text-muted-foreground hover:text-white transition-colors"
                data-testid="link-terms"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 text-center">
          <p className="text-muted-foreground text-sm">
            © 2026 LeadCrew. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Back to Top Button
const BackToTopButton = ({ isVisible }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      onClick={scrollToTop}
      className={`back-to-top ${isVisible ? "visible" : ""}`}
      data-testid="back-to-top"
      aria-label="Back to top"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
};

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Scroll to top on page load/refresh
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="App min-h-screen bg-[#020617]" data-testid="app">
      <Toaster position="top-center" richColors />
      <Header
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <main>
        <HeroSection />
        <HowItWorksSection />
        <UseCasesSection />
        <BenefitsSection />
        <SocialProofSection />
        <ContactSection />
      </main>
      <Footer />
      <BackToTopButton isVisible={showBackToTop} />
    </div>
  );
}

export default App;
